// Formato y agrupación de los campos del reporte de cliente (/reporte/cliente).
// Lo comparten la tabla (NewReporte.vue) y el panel de detalle
// (components/Reporte/DetallePagoDrawer.vue). El Excel no usa esto: mantiene
// su propio formatPago con las 50 columnas.

import formatDate from "../../../utils/formatDate";
import formatImport, { formatImportFloat } from "../../../utils/formatImport";
import { headers } from "./headerReporte";

// Tipo de cada campo. Las unidades salen del backend:
// - centavos: importes en entities.Monto o crudos de MySQL (la mayoría).
// - pesos: iva_cft y los cuatro topes del tarifario vienen en pesos con centavos.
// - factor: porcentajes como factor (0,0215 = 2,15 %).
// - coeficiente: porcentaje_costo_servicio llega como 1,07 = 7 %.
const TIPOS = {
  fecha_pago: "fecha",
  fecha_presentacion: "fecha",
  fecha_acreditacion: "fecha",
  fecha_rendicion: "fecha",

  importe_a_pagar: "centavos",
  importe_pagado: "centavos",
  retencion_iibb: "centavos",
  retencion_iva: "centavos",
  retencion_ganancias: "centavos",
  importe_cft: "centavos",
  costo_financiero: "centavos",
  iva_s_costo_financiero: "centavos",
  comision_proveedor: "centavos",
  iva_comision_proveedor: "centavos",
  comision_proveedor_prisma: "centavos",
  comision_telco: "centavos",
  iva_comision_telco: "centavos",
  comision_total: "centavos",
  iva_comision_total: "centavos",
  importe_neto_cobrado: "centavos",
  importe_neto_cobrado_interno: "centavos",
  importe_rendido: "centavos",
  monto_costo_servicio: "centavos",

  iva_cft: "pesos",
  importe_minimo: "pesos",
  importe_maximo: "pesos",
  importe_minimo_telco: "pesos",
  importe_maximo_telco: "pesos",

  porcentaje_retencion_iibb: "factor",
  porcentaje_retencion_iva: "factor",
  porcentaje_retencion_ganancias: "factor",
  porcentaje_comision_proveedor: "factor",
  porcentaje_comision_proveedor_prisma: "factor",
  comision_porcentaje_telco: "factor",
  alicuota_iva: "factor",

  porcentaje_costo_servicio: "coeficiente",
};

const VACIO = "—";

const fmtPorcentaje = (factor) =>
  (factor * 100).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " %";

// Formatea un campo del pago para mostrarlo en pantalla.
export function formatearCampo(campo, valor) {
  const tipo = TIPOS[campo];
  if (tipo === "fecha") {
    return valor && valor !== 0 ? formatDate(String(valor).substr(0, 10)) : VACIO;
  }
  if (tipo === "centavos") return formatImport(valor || 0);
  if (tipo === "pesos") return formatImportFloat(valor || 0);
  if (tipo === "factor") return valor ? fmtPorcentaje(valor) : VACIO;
  if (tipo === "coeficiente") {
    return valor > 0 ? fmtPorcentaje(Math.round((valor - 1) * 1e4) / 1e4) : VACIO;
  }
  if (valor === null || valor === undefined || valor === "" || valor === "-") return VACIO;
  return valor;
}

// Columnas que en Prisma salen del archivo de conciliación (arancel y neto
// interno): mientras Prisma no informó la operación el backend las marca con
// arancel_pendiente y se muestran vacías en lugar de un valor estimado.
const CAMPOS_ARANCEL = [
  "porcentaje_comision_proveedor",
  "comision_proveedor",
  "iva_comision_proveedor",
  "importe_neto_cobrado_interno",
];

// Indica si el campo no tiene valor para este pago: arancel de Prisma todavía
// no informado, o neto cobrado que el operador no informa por operación.
export function campoNoAplica(campo, pago) {
  if (!pago) return false;
  if (pago.arancel_pendiente && CAMPOS_ARANCEL.includes(campo)) return true;
  if (pago.sin_neto_operador && campo === "importe_neto_cobrado") return true;
  return false;
}

// Valor formateado de un campo de un pago, respetando lo que no aplica.
export function valorCampo(campo, pago) {
  if (campoNoAplica(campo, pago)) return VACIO;
  return formatearCampo(campo, pago && pago[campo]);
}

// Columnas numéricas: se alinean a la derecha en la tabla.
export const esNumerico = (campo) =>
  ["centavos", "pesos", "factor", "coeficiente"].includes(TIPOS[campo]);

// ─── Estado del pago ──────────────────────────────────────────────────
// Semáforo del backoffice Wee! (verde institucional TelCo para éxito; naranja
// y rojo son convenciones de UI que la marca no redefine).
const TONO_ESTADO = {
  AUTORIZADO: { color: "#009238", bg: "#E6F4EB", icono: "mdi-check-circle-outline" },
  APROBADO: { color: "#009238", bg: "#E6F4EB", icono: "mdi-check-circle-outline" },
  ACREDITADO: { color: "#009238", bg: "#E6F4EB", icono: "mdi-check-circle-outline" },
  REVERTIDO: { color: "#e65100", bg: "#fff3e0", icono: "mdi-undo-variant" },
  RECHAZADO: { color: "#c62828", bg: "#ffebee", icono: "mdi-close-circle-outline" },
  CADUCADO: { color: "#c62828", bg: "#ffebee", icono: "mdi-clock-alert-outline" },
  PENDIENTE: { color: "#607d8b", bg: "#eceff1", icono: "mdi-clock-outline" },
  PROCESANDO: { color: "#00B0EA", bg: "#E6F7FD", icono: "mdi-progress-clock" },
};
const TONO_NEUTRO = { color: "#607d8b", bg: "#eceff1", icono: "mdi-circle-outline" };

export const tonoEstado = (estado) =>
  TONO_ESTADO[String(estado || "").toUpperCase()] || TONO_NEUTRO;

// Etiqueta legible: "AUTORIZADO" -> "Autorizado".
export const labelEstado = (estado) => {
  const e = String(estado || "").trim();
  return e ? e.charAt(0).toUpperCase() + e.slice(1).toLowerCase() : "Sin estado";
};

// ─── Vistas por tema ──────────────────────────────────────────────────
// Columnas de contexto que repiten todas las vistas temáticas, para saber de
// qué pago es cada fila sin volver al resumen.
const CONTEXTO = ["fecha_pago", "pago_id", "estado", "importe_pagado"];

const header = (value) => {
  const h = headers.find((x) => x.value === value);
  return { text: h ? h.text : value, value };
};

const conAlineacion = (h) => ({
  ...h,
  align: esNumerico(h.value) ? "end" : "start",
  // Vuetify pone el ancho fijo de headerReporte; en las vistas temáticas
  // dejamos que la tabla lo resuelva.
  width: undefined,
});

// Resumen: 8 columnas compuestas (cada celda arma su propio contenido en
// NewReporte.vue) más el acceso al detalle.
const RESUMEN = [
  { text: "Fecha", value: "fecha_pago" },
  { text: "Pago", value: "pago_id" },
  { text: "Cliente", value: "cliente" },
  { text: "Medio", value: "medio_pago" },
  { text: "Estado", value: "estado" },
  { text: "Pagado", value: "importe_pagado", align: "end" },
  { text: "Neto cobrado", value: "importe_neto_cobrado", align: "end" },
  { text: "Rendido", value: "importe_rendido", align: "end" },
  { text: "", value: "detalle", align: "end", width: 40 },
];

export const VISTAS = [
  {
    key: "resumen",
    text: "Resumen",
    icono: "mdi-view-dashboard-outline",
    headers: RESUMEN,
  },
  {
    key: "comisiones",
    text: "Comisiones",
    icono: "mdi-percent-outline",
    headers: [
      ...CONTEXTO,
      "porcentaje_comision_proveedor",
      "comision_proveedor",
      "iva_comision_proveedor",
      "comision_porcentaje_telco",
      "comision_telco",
      "iva_comision_telco",
      "comision_total",
      "iva_comision_total",
      "importe_neto_cobrado",
      "importe_neto_cobrado_interno",
    ].map((v) => conAlineacion(header(v))),
  },
  {
    key: "retenciones",
    text: "Retenciones",
    icono: "mdi-scale-balance",
    headers: [
      ...CONTEXTO,
      "porcentaje_retencion_iibb",
      "retencion_iibb",
      "porcentaje_retencion_iva",
      "retencion_iva",
      "porcentaje_retencion_ganancias",
      "retencion_ganancias",
      "importe_rendido",
    ].map((v) => conAlineacion(header(v))),
  },
  {
    key: "completa",
    text: "Completa",
    icono: "mdi-table-large",
    // Todas las columnas de antes, salvo las cuatro de porcentaje de arancel
    // mínimo/máximo, que el backend no envía (salían siempre vacías).
    headers: headers
      .filter((h) => !/^porcentaje_arancel_(min|max)/.test(h.value))
      .map(conAlineacion),
  },
];
